import React, { useEffect } from 'react';

import useServerStateContext from '@/shared/hooks/useServerStateContext';
import Box from '@mui/material/Box';

import PtzJoystick from './PtzJoystick';

import { usePTZSocket } from '@/shared/hooks/websocket/usePTZSocket';
import { ViewType } from '@/features/camera/types/VideoSource';
import { ZOOM_FACTOR } from '../constants';

const OPACITY_WHEN_DISABLED = 0.4;
const MAX_ZOOM_SPEED = 1.3; 

const PtzControls = ({ sx, zoomOnly }: { sx?: object; zoomOnly?: boolean }) => {
  const { selectedCameraId: cameraId, viewType } = useServerStateContext();
  const { setValue: setPtzVelocity } = usePTZSocket();
  const disabled =
    cameraId === null || typeof cameraId === 'undefined' || viewType === ViewType.MOSAIC;

  const [joystick, setJoystick] = React.useState<Gamepad | null>(null);

  const handlePanTiltChange = (pan: number, tilt: number) => {
    setPtzVelocity({
      pan,
      tilt,
      zoom: 0,
    });
  };

  // Handle zoom from SpringSlider
  // zoomDiff is normalized (-1 to +1): negative = zoom IN (up), positive = zoom OUT (down)
  const handleZoomChange = React.useCallback(
    (zoomDiff: number) => {
      // Scale to match keyboard: zoomDiff * MAX_ZOOM_SPEED / ZOOM_FACTOR
      // At full slider travel (±1), this matches keyboard at max hold time
      const zoomVelocity = (zoomDiff * MAX_ZOOM_SPEED) / ZOOM_FACTOR;
      setPtzVelocity({
        pan: 0,
        tilt: 0,
        zoom: zoomVelocity,
      });
    },
    [setPtzVelocity],
  );

  function gamepadHandler(e: GamepadEvent, connected: boolean) {
    const gamepad = e.gamepad;
    // Note:
    // gamepad === navigator.getGamepads()[gamepad.index]

    if (connected) {
      setJoystick(gamepad);
    } else {
      setJoystick(null);
    }
  }

  useEffect(() => {
    window.addEventListener(
      'gamepadconnected',
      (e) => {
        gamepadHandler(e, true);
      },
      false,
    );
    window.addEventListener(
      'gamepaddisconnected',
      (e) => {
        gamepadHandler(e, false);
      },
      false,
    );

    return () => {
      window.removeEventListener(
        'gamepadconnected',
        (e) => {
          gamepadHandler(e, true);
        },
        false,
      );
      window.removeEventListener(
        'gamepaddisconnected',
        (e) => {
          gamepadHandler(e, false);
        },
        false,
      );
    };
  }, []);

  return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center', opacity: disabled ? OPACITY_WHEN_DISABLED : 1 , backgroundColor: '#101013', borderRadius: 1, p: 1, ...sx }}>
        <PtzJoystick disabled={disabled} onChange={handlePanTiltChange} onZoomChange={handleZoomChange} zoomOnly={zoomOnly} />
      </Box>
  );
};

export default PtzControls;
